import { SITE } from '@/lib/site'
import { PATHS } from '@/routes/paths'
import { SEO_SITE_NAME } from '../defaults'
import { absoluteUrl, getSiteOrigin } from '../utils'

export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SEO_SITE_NAME,
    url: getSiteOrigin(),
    logo: absoluteUrl(SITE.logo),
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: SITE.phoneTel,
      contactType: 'customer service',
      email: SITE.email,
      areaServed: 'IN',
    },
    sameAs: Object.values(SITE.social),
  }
}

export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SEO_SITE_NAME,
    url: getSiteOrigin(),
    potentialAction: {
      '@type': 'SearchAction',
      target: `${getSiteOrigin()}/properties?search={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
}

/** FAQ rich results — answers are plain text (HTML stripped). */
export function faqPageSchema(faqs: Array<{ question: string; answer: string }>) {
  const entries = faqs
    .map((f) => ({
      question: f.question?.trim() ?? '',
      answer: (f.answer ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim(),
    }))
    .filter((f) => f.question && f.answer)
  if (entries.length === 0) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  }
}

export function breadcrumbSchema(items: Array<{ name: string; url?: string }>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      ...(item.url ? { item: absoluteUrl(item.url) } : {}),
    })),
  }
}

export function contactPageSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Quick Build Con',
    url: absoluteUrl(PATHS.contact),
    mainEntity: { '@id': `${getSiteOrigin()}/#localbusiness` },
  }
}

/** Free web tools (cost estimator, loan calculators, guides). */
export function webApplicationSchema(input: {
  name: string
  path: string
  description: string
  applicationCategory?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: input.name,
    url: absoluteUrl(input.path),
    description: input.description,
    applicationCategory: input.applicationCategory ?? 'BusinessApplication',
    operatingSystem: 'Any',
    browserRequirements: 'Requires JavaScript',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
    },
    provider: {
      '@type': 'Organization',
      name: SEO_SITE_NAME,
      url: getSiteOrigin(),
    },
  }
}

export function webPageSchema(input: { name: string; path: string; description: string }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: input.name,
    url: absoluteUrl(input.path),
    description: input.description,
    isPartOf: {
      '@type': 'WebSite',
      name: SEO_SITE_NAME,
      url: getSiteOrigin(),
    },
  }
}

/** Single NAP source for local pack / map eligibility. */
export function localBusinessSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': ['RealEstateAgent', 'LocalBusiness'],
    '@id': `${getSiteOrigin()}/#localbusiness`,
    name: SITE.legalName,
    alternateName: SITE.name,
    url: getSiteOrigin(),
    logo: absoluteUrl(SITE.logo),
    image: absoluteUrl(SITE.logo),
    telephone: SITE.phoneTel,
    email: SITE.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.nap.street,
      addressLocality: SITE.nap.locality,
      addressRegion: SITE.nap.region,
      postalCode: SITE.nap.postalCode,
      addressCountry: SITE.nap.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: SITE.nap.latitude,
      longitude: SITE.nap.longitude,
    },
    openingHoursSpecification: SITE.hours.map((slot) => ({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: slot.days,
      opens: slot.opens,
      closes: slot.closes,
    })),
    areaServed: {
      '@type': 'AdministrativeArea',
      name: 'Gujarat, India',
    },
    sameAs: Object.values(SITE.social),
    priceRange: '₹',
  }
}
